import type { PrimeVueConfiguration, PrimeVueLocaleOptions } from 'primevue/config'
import type { SupportedLocales } from '../types/i18n'
import { i18n } from '../index'

/**
 * Helper function to translate a key without TypeScript recursion issues
 * Using 'any' to avoid vue-i18n type recursion (TS2589)
 */
function translate(key: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return String((i18n.global as any).t(key))
}

/**
 * Uppercases the first letter - Intl returns lowercase month/day names for
 * locales like Italian and Spanish, while PrimeVue's own defaults are capitalized
 */
function capitalize(value: string): string {
  return value.charAt(0).toLocaleUpperCase() + value.slice(1)
}

/**
 * Localized month names, January first, as expected by PrimeVue
 */
function getMonthNames(locale: SupportedLocales, month: 'long' | 'short'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { month, timeZone: 'UTC' })
  return Array.from({ length: 12 }, (_, index) =>
    capitalize(formatter.format(new Date(Date.UTC(2021, index, 1)))),
  )
}

/**
 * Localized day names, Sunday first, as expected by PrimeVue.
 * 2021-08-01 (UTC) is a Sunday, so the week starts there.
 */
function getDayNames(locale: SupportedLocales, weekday: 'long' | 'short'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday, timeZone: 'UTC' })
  return Array.from({ length: 7 }, (_, index) =>
    capitalize(formatter.format(new Date(Date.UTC(2021, 7, 1 + index)))),
  )
}

/**
 * Two-letter day names for the calendar header. Derived from the short names
 * rather than Intl's 'narrow' form, which yields a single ambiguous letter
 * (en: S M T W T F S, it: D L M M G V S)
 */
function getDayNamesMin(locale: SupportedLocales): string[] {
  return getDayNames(locale, 'short').map((name) => name.slice(0, 2))
}

/**
 * Creates PrimeVue locale configuration from i18n translations
 */
export function createPrimeVueLocaleConfig(locale: SupportedLocales) {
  return {
    clear: translate('common.clear'),
    apply: translate('common.apply'),
    cancel: translate('common.cancel'),
    choose: translate('common.choose'),
    upload: translate('common.upload'),
    today: translate('common.calendar.today'),
    weekHeader: translate('common.calendar.weekHeader'),
    chooseDate: translate('common.calendar.chooseDate'),
    chooseMonth: translate('common.calendar.chooseMonth'),
    chooseYear: translate('common.calendar.chooseYear'),
    prevMonth: translate('common.calendar.prevMonth'),
    nextMonth: translate('common.calendar.nextMonth'),
    prevYear: translate('common.calendar.prevYear'),
    nextYear: translate('common.calendar.nextYear'),
    prevDecade: translate('common.calendar.prevDecade'),
    nextDecade: translate('common.calendar.nextDecade'),
    firstDayOfWeek: locale === 'en' ? 0 : 1, // Sunday for English, Monday for others
    dateFormat: locale === 'en' ? 'mm/dd/yy' : 'dd/mm/yy',
    monthNames: getMonthNames(locale, 'long'),
    monthNamesShort: getMonthNames(locale, 'short'),
    dayNames: getDayNames(locale, 'long'),
    dayNamesShort: getDayNames(locale, 'short'),
    dayNamesMin: getDayNamesMin(locale),
  }
}

/**
 * Applies a locale to PrimeVue's reactive config. PrimeVue only accepts a locale as an
 * install option, so this is how a locale change after mount reaches the date picker.
 * Spreading the current locale keeps every key this app does not translate (filter
 * operators, aria labels) at PrimeVue's own defaults.
 *
 * The instance is passed in rather than resolved through usePrimeVue(), which injects and
 * would therefore only work inside a component setup
 */
export function applyPrimeVueLocale(
  primeVue: { config: PrimeVueConfiguration },
  locale: SupportedLocales,
): void {
  // config.locale is always populated: PrimeVue deep-merges the install option over its
  // own English defaults, so the keys the type requires are present at runtime
  primeVue.config.locale = {
    ...(primeVue.config.locale as PrimeVueLocaleOptions),
    ...createPrimeVueLocaleConfig(locale),
  }
}
